import type { ThemeType } from "@packages/ui";
import { spacing, useIsDesktopViewport, useTheme } from "@packages/ui";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

export const ScrollHighlighter = ({ selector }: { selector: string }) => {
  const isDesktop = useIsDesktopViewport();
  const [top, setTop] = useState<number | null>(null);
  const [bottom, setBottom] = useState<number | null>(null);
  const [visible, setVisible] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const { themeContainerRef } = useTheme();

  const transform = useMemo(() => {
    const NEGATIVE_HALF_DESKTOP_PLUS_SPACE =
      -(parseInt(spacing.maxReaderWidth) / 2) - 5;
    const NON_ZERO_VALUE = -100;

    if (isDesktop) return NEGATIVE_HALF_DESKTOP_PLUS_SPACE;
    if (typeof window !== "undefined") {
      return -window.innerWidth / 2;
    }
    return NON_ZERO_VALUE;
  }, [isDesktop]);

  useEffect(() => {
    const adjust = () => {
      const elems = document.querySelectorAll(selector);
      let top: number | null = null;
      let bottom: number | null = null;
      for (const elem of elems) {
        const box = elem.getBoundingClientRect();
        if (top === null) top = box.top;
        else if (box.top < top) top = box.top;
        if (bottom === null) bottom = box.bottom;
        else if (box.bottom > bottom) bottom = box.bottom;
      }
      setTop(top);
      setBottom(bottom);
    };

    if (selector?.length && typeof document !== "undefined") {
      const elem = document.querySelector(selector);
      if (elem) {
        let timer: ReturnType<typeof setTimeout> | null = null;
        let adjuster: ReturnType<typeof setTimeout> | null = null;
        let raf: ReturnType<typeof requestAnimationFrame> | null = null;
        adjust();
        raf = requestAnimationFrame(() => {
          setVisible(true);
          adjuster = setInterval(adjust, 250);
          timer = setTimeout(() => {
            setVisible(false);
            clearInterval(adjuster);
          }, 5000);
        });

        return () => {
          cancelAnimationFrame(raf);
          clearTimeout(timer);
          clearInterval(adjuster);
        };
      }
    }
  }, [selector]);

  useEffect(() => {
    if (themeContainerRef.current) {
      setMounted(true);

      return () => {
        setMounted(false);
      };
    }
  }, []);

  if (!mounted) return null;

  return createPortal(
    <div
      css={[
        (theme: ThemeType) => ({
          background: theme.semanticColors.accentMedLow,
          borderRadius: theme.radius.s,
          height: `${bottom - top}px`,
          left: theme.numericPercentages[50],
          opacity: 0,
          position: "fixed",
          top: `${top}px`,
          transform: `translate(${transform}px)`,
          transitionDuration: "250ms",
          transitionProperty: "opacity",
          transitionTimingFunction: "ease-in",
          width: theme.spacing.s,
        }),
        visible
          ? {
              opacity: 1,
              transitionProperty: "opacity, top",
              transitionTimingFunction: "ease-out",
            }
          : null,
      ]}
    />,
    themeContainerRef.current,
  );
};
